import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ImgIx from 'react-imgix';
import cx from 'classnames';

import { applySessionReplayMask } from 'Services/datadog/datadogRum';

import { NoImage } from './NoImage';

import styles from './Image.module.scss';

interface ImageProps {
	ariaLabel?: string;
	disableSrcSet?: boolean;
	loading?: 'lazy' | 'eager';
	onClick?: () => void;
	onError?: () => void;
	shouldMaskImage?: boolean;
	sizes?: string;
	skipTransform?: boolean;
	src: string;
	widths?: number[];
}

const SLOW_LOADING_TIME = 300;

export const Image: React.FC<ImageProps> = ({
	ariaLabel,
	disableSrcSet,
	loading,
	onClick,
	onError,
	shouldMaskImage,
	sizes,
	skipTransform,
	src,
	widths,
}) => {
	const [isLoadingFailed, setIsLoadingFailed] = useState(false);
	const [isLoadingSlow, setIsLoadingSlow] = useState(false);
	const [isReady, setIsReady] = useState(false);

	const signed = useMemo(() => {
		const url = new URL(src);
		const params = Object.fromEntries(url.searchParams.entries());
		return !!params.s;
	}, [src]);

	useEffect(() => {
		const timer = setTimeout(() => setIsLoadingSlow(true), SLOW_LOADING_TIME);
		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		// eslint-disable-next-line no-unused-expressions
		isLoadingFailed && onError?.();
	}, [isLoadingFailed, onError]);

	// @ts-expect-error wrong type in imgix onMounted method
	const handleRef = useCallback((ref) => {
		if (ref.complete) {
			const hasLoadingFailed = ref.naturalHeight === 0;
			setIsReady(true);
			setIsLoadingFailed(hasLoadingFailed);
		}
	}, []);

	const handleClick = () => {
		// eslint-disable-next-line no-unused-expressions
		!isLoadingFailed && onClick?.();
	};

	const maskParams = { ...(shouldMaskImage ? applySessionReplayMask({ maskLevel: 'mask' }) : {}) };

	return (
		<>
			{signed ? (
				<img
					ref={(elm) => (elm ? handleRef(elm) : undefined)}
					alt={ariaLabel}
					aria-label={ariaLabel}
					className={cx(styles.image, { [styles.ready]: isReady })}
					loading={loading}
					onClick={handleClick}
					onError={() => setIsLoadingFailed(true)}
					onLoad={() => setIsReady(true)}
					src={src}
					{...maskParams}
				/>
			) : (
				<ImgIx
					disableLibraryParam
					className={cx(styles.image, { [styles.ready]: isReady })}
					disableQualityByDPR={skipTransform}
					disableSrcSet={disableSrcSet || skipTransform}
					htmlAttributes={{
						'aria-label': ariaLabel,
						loading,
						onClick: handleClick,
						onError: () => setIsLoadingFailed(true),
						onLoad: () => setIsReady(true),
						...maskParams,
					}}
					imgixParams={{
						auto: skipTransform ? undefined : 'compress,format',
					}}
					onMounted={handleRef}
					sizes={sizes}
					src={src}
					srcSetOptions={{
						widths,
					}}
				/>
			)}
			{loading === 'lazy' && (
				<div
					className={cx('swiper-lazy-preloader', styles.loader, {
						[styles.show]: isLoadingSlow && !isLoadingFailed && !isReady,
					})}
				/>
			)}
			{isLoadingFailed && <NoImage />}
		</>
	);
};
